import {
  buildNavItemFromRoute,
  buildRoute,
  RouteAuthenticationRequirement,
  RouteError,
  useAuth,
  useNavItemStore
} from '@cks-internal/react-base';
import type { RouteItem } from '@cks-internal/react-base';
import type { BaseNavItem } from '@cks-internal/react-base/dist/BaseComponents/Nav/types';
import { useEffect, useState } from 'react';
import { RouteObject } from 'react-router-dom';
import { Layout } from '../components/layout/layout.tsx';
import { AccountManagementItems, LoginRouteItem, LogoutRouteItem, MyAccountRouteItem } from './AccountRoutes';
import { getLegacyNavItems, LegacyHiddenRoutes, TimesheetLockedOutNavItems } from './LegacyRoutes';
import { DevHiddenRoutes } from './DevRoutes';
import { Home } from '../components/layout/home.tsx';
import { sortNavSubitemsAlphabetically } from '../utils/sortNavSubitemsAlphabetically';
import { CompassUser } from '../globalTypes/user/compassUser.ts';
import { AppRoles } from '../constants/appRoles.ts';
import { ApplicationRoutes } from '../constants/appRoutes.ts';
import { openQuickLink } from '../features/v2/quickLinks/apis/useQuickLinksMutations.ts';
import { fetchQuickLinksNavigation } from '../features/v2/quickLinks/apis/useQuickLinksNavigationQuery.ts';
import type { QuickLinkCategoryModel } from '../features/v2/quickLinks/types/QuickLinkModels.ts';

const UnauthenticatedRouteItems: RouteItem[] = [LoginRouteItem, AccountManagementItems];

const createQuickLinksNavItem = (navItem: BaseNavItem, quickLinkCategories: QuickLinkCategoryModel[]): BaseNavItem => {
  const documentItems = quickLinkCategories.flatMap((category) =>
    (category.Links ?? []).map<BaseNavItem>((link) => ({
      text: `${category.Category} - ${link.DisplayName}`,
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [],
      onClick: () => {
        void openQuickLink(link).catch(() => undefined);
      }
    }))
  );

  if (documentItems.length === 0) {
    return navItem;
  }

  return {
    ...navItem,
    path: ApplicationRoutes.quickLinks.overview,
    subItems: [
      {
        text: 'All Quick Links',
        authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
        allowedRoles: [],
        path: ApplicationRoutes.quickLinks.overview
      },
      ...documentItems
    ]
  } as unknown as BaseNavItem;
};

export function useNavWithRouting() {
  const setNavItemStore = useNavItemStore((state) => state.set);
  const auth = useAuth<CompassUser>();
  const isTimesheetLockedOut = auth.user?.Roles.includes(AppRoles.TimesheetLockedOut) ?? false;
  const [quickLinkCategories, setQuickLinkCategories] = useState<QuickLinkCategoryModel[]>([]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      setQuickLinkCategories([]);
      return;
    }

    let isCurrent = true;

    fetchQuickLinksNavigation()
      .then((categories) => {
        if (isCurrent) {
          setQuickLinkCategories(categories.filter((category) => (category.Links?.length ?? 0) > 0));
        }
      })
      .catch(() => {
        if (isCurrent) {
          setQuickLinkCategories([]);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [auth.isAuthenticated]);

  const authenticatedRouteItems: RouteItem[] = [
    AccountManagementItems,
    MyAccountRouteItem,
    LogoutRouteItem,
    ...(isTimesheetLockedOut ? TimesheetLockedOutNavItems : getLegacyNavItems(auth.user?.IsHr ?? false))
  ];
  const hiddenRouteItems = isTimesheetLockedOut ? [] : LegacyHiddenRoutes;
  const routes = new Array<RouteObject>();
  const nav: BaseNavItem[] = [];

  routes.push({
    path: '/',
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      {
        path: '/',
        element: <Home />,
        errorElement: <RouteError />
      }
    ]
  });

  authenticatedRouteItems.forEach((value) => {
    routes.push(buildRoute(value));
    const navItem = buildNavItemFromRoute(value);
    if (navItem) {
      const isQuickLinksRoute = value.path === ApplicationRoutes.quickLinks.overview;
      nav.push(isQuickLinksRoute ? createQuickLinksNavItem(navItem, quickLinkCategories) : navItem);
    }
  });

  // Add hidden routes (not shown in nav but accessible via URL)
  hiddenRouteItems.forEach((value) => {
    routes.push(buildRoute(value));
  });

  if (!isTimesheetLockedOut) {
    DevHiddenRoutes.forEach((value) => {
      routes.push(buildRoute(value));
    });
  }

  // Catch-all for any legacy URL not covered by explicit routes
  if (!isTimesheetLockedOut) {
    routes.push({
      path: '/legacy/*',
      element: <Layout />,
      errorElement: <RouteError />,
      children: [
        {
          path: '*',
          async lazy() {
            const { LegacyIframeCatchAll } = await import('../components/common/LegacyIframe');
            return { Component: LegacyIframeCatchAll };
          }
        }
      ]
    });
  }

  UnauthenticatedRouteItems.forEach((value) => {
    routes.push(buildRoute(value));
    const navItem = buildNavItemFromRoute(value);
    if (navItem) {
      nav.push(navItem);
    }
  });

  setNavItemStore(sortNavSubitemsAlphabetically(nav));

  return routes;
}
